import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import DeleteConfirmModal from '../layouts/DeleteConfirmModal';
import { getPatientBills, cancelBill, addPayment } from '../../actions/billingActions';
import { getpatient } from '../../actions/patientActions';
import {
    clearPaymentAdded,
    clearBillDeleted,
    clearBillCreated,
    clearBillingError
} from "../../slices/billingSlice";
import { downloadBillPDF } from "../../utils/billPdf";
import { Link } from "react-router-dom";
import formatDate from "../../utils/formatDate";
import InvoicePreviewModal from "../billing/InvoicePreviewModal";

const currency = (n) =>
    `\u20b9${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const statusBadge = (status) => {
    const map = {
        Pending: "bg-secondary",
        "Partially Paid": "bg-warning text-dark",
        Paid: "bg-success",
        Cancelled: "bg-danger"
    };
    return map[status] || "bg-secondary";
};

const getPaymentMethod = (b) => {
    if (b.payments && b.payments.length > 0) {
        const last = b.payments[b.payments.length - 1];
        if (last && last.method) return last.method;
    }
    if (b.paymentMethod && b.paymentMethod !== "—" && String(b.paymentMethod).trim() !== "") {
        return b.paymentMethod;
    }
    return "Cash";
};

export default function PatientBilling({ patient }) {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.authState);
    const { bills, summary, loading, error, isPaymentAdded, isBillDeleted, isBillCreated } =
        useSelector((state) => state.billingState);

    const isAdmin = user?.role === "admin" || user?.role === "master";

    const [payFor, setPayFor] = useState(null); // bill id currently taking a payment
    const [payAmount, setPayAmount] = useState("");
    const [payMethod, setPayMethod] = useState("Cash");
    const [payRef, setPayRef] = useState("");
    const [previewBill, setPreviewBill] = useState(null);

    const targetPatientId = patient?.patientId || patient?._id || patient?.id;

    useEffect(() => {
        if (targetPatientId) dispatch(getPatientBills(targetPatientId));
    }, [dispatch, targetPatientId]);

    useEffect(() => {
        dispatch(clearPaymentAdded());
        dispatch(clearBillDeleted());
        dispatch(clearBillingError());
        return () => {
            dispatch(clearPaymentAdded());
            dispatch(clearBillDeleted());
            dispatch(clearBillingError());
        };
    }, [dispatch]);

    useEffect(() => {
        if (error) {
            toast(error, { type: "error" });
            dispatch(clearBillingError());
        }
    }, [error, dispatch]);

    useEffect(() => {
        if (isPaymentAdded) {
            toast("Payment recorded", { type: "success" });
            dispatch(clearPaymentAdded());
            setPayFor(null);
            setPayAmount("");
            setPayRef("");
            if (targetPatientId) {
                dispatch(getPatientBills(targetPatientId));
                dispatch(getpatient(targetPatientId));
            }
        }
    }, [isPaymentAdded, dispatch, targetPatientId]);

    useEffect(() => {
        if (isBillDeleted) {
            toast("Bill cancelled", { type: "success" });
            dispatch(clearBillDeleted());
            if (targetPatientId) {
                dispatch(getPatientBills(targetPatientId));
                dispatch(getpatient(targetPatientId));
            }
        }
    }, [isBillDeleted, dispatch, targetPatientId]);
    useEffect(() => {
        if (isBillCreated) {
            dispatch(clearBillCreated());
            if (targetPatientId) {
                dispatch(getPatientBills(targetPatientId));
                dispatch(getpatient(targetPatientId));
            }
        }
    }, [isBillCreated, dispatch, targetPatientId]);

    const submitPayment = (billId, balanceDue) => {
        const amount = Number(payAmount);
        if (!amount || amount <= 0) {
            toast("Enter a valid payment amount", { type: "error" });
            return;
        }
        if (amount > balanceDue) {
            toast(`Amount exceeds balance due (${currency(balanceDue)})`, { type: "error" });
            return;
        }
        dispatch(addPayment(billId, { amount, method: payMethod, reference: payRef }));
    };

    const [deleteModal, setDeleteModal] = useState({ show: false, id: null, name: '' });

    const handleCancelBill = (billId, invoiceNumber) => {
        setDeleteModal({ show: true, id: billId, name: invoiceNumber });
    };

    const confirmDeleteBill = () => {
        dispatch(cancelBill(deleteModal.id));
        setDeleteModal({ show: false, id: null, name: '' });
    };

    const handleSendWhatsApp = (bill) => {
        const baseUrl = window.location.origin;
        const tokenParam = bill.shareToken ? `?token=${encodeURIComponent(bill.shareToken)}` : '';
        const invoiceLink = `${baseUrl}/invoice/${bill._id}${tokenParam}`;
        const patientName = patient?.name || 'Valued Patient';

        let cleanPhone = String(patient?.phone || '').replace(/\D/g, '');
        if (cleanPhone.startsWith('0')) cleanPhone = cleanPhone.replace(/^0+/, '');
        if (cleanPhone.length === 10) cleanPhone = `91${cleanPhone}`;

        const websiteUrl = 'https://greennaturopathy.com';
        const reviewUrl = 'https://g.page/r/CfNAbnnuCG74EAI/review';
        const messageBody = `Dear ${patientName},\n\nThank you for visiting Green Naturopathy.\n\nYour official invoice (${bill.invoiceNumber}) is ready.\n\n📄 View and download your invoice:\n${invoiceLink}\n\n⭐ Share your feedback / Rate us on Google:\n${reviewUrl}\n\n🌐 Visit our official website:\n${websiteUrl}\n\nHave a healthy and wonderful day!\n— Green Naturopathy`;
        const encodedText = encodeURIComponent(messageBody);

        const waUrl = cleanPhone
            ? `https://api.whatsapp.com/send?phone=${cleanPhone}&text=${encodedText}`
            : `https://api.whatsapp.com/send?text=${encodedText}`;
        window.open(waUrl, '_blank', 'noopener,noreferrer');
    };

    return (
        <Fragment>
            <div className="d-flex justify-content-between align-items-center mt-4 mb-3 flex-wrap gap-2">
                <h5 className="mb-0 fw-bold">Billing</h5>
                {isAdmin && (
                    <Link to="/admin/billing/new" state={{ patient }} className="btn btn-primary btn-sm mb-0">
                        + New Bill
                    </Link>
                )}
            </div>

            <div className="row g-2 mb-3">
                <div className="col-4">
                    <div className="border rounded p-2 text-center">
                        <div className="text-muted small text-truncate">Total Billed</div>
                        <div className="fw-bold text-truncate" title={currency(summary?.totalBilled)}>{currency(summary?.totalBilled)}</div>
                    </div>
                </div>
                <div className="col-4">
                    <div className="border rounded p-2 text-center">
                        <div className="text-muted small text-truncate">Total Paid</div>
                        <div className="fw-bold text-success text-truncate" title={currency(summary?.totalPaid)}>{currency(summary?.totalPaid)}</div>
                    </div>
                </div>
                <div className="col-4">
                    <div className="border rounded p-2 text-center">
                        <div className="text-muted small text-truncate">Balance Due</div>
                        <div className={`fw-bold text-truncate ${Number(summary?.totalDue || 0) > 0 ? 'text-danger' : 'text-success'}`} title={currency(summary?.totalDue)}>
                            {currency(summary?.totalDue)}
                        </div>
                    </div>
                </div>
            </div>

            {bills && bills.length > 0 ? (
                <div className="table-responsive">
                    <table className="table table-sm table-bordered align-middle">
                        <thead>
                            <tr>
                                <th>Invoice #</th>
                                <th>Date</th>
                                <th>Total</th>
                                <th>Paid</th>
                                <th>Balance Due</th>
                                <th>Method of Payment</th>
                                <th>Status</th>
                                {isAdmin && <th>Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {bills.map((b) => (
                                <Fragment key={b._id}>
                                    <tr>
                                        <td>{b.invoiceNumber}</td>
                                        <td>{formatDate(b.createdAt)}</td>
                                        <td>{currency(b.totalAmount)}</td>
                                        <td>{currency(b.amountPaid)}</td>
                                        <td className={Number(b.balanceDue || 0) > 0 ? 'text-danger fw-semibold' : 'text-muted'}>
                                            {currency(b.balanceDue)}
                                        </td>
                                        <td>
                                            <span className="badge bg-light text-dark border">
                                                {getPaymentMethod(b)}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`badge ${statusBadge(b.status)}`}>{b.status}</span>
                                        </td>
                                        {isAdmin && (
                                            <td>
                                                {b.status !== "Paid" && b.status !== "Cancelled" && (
                                                    <button
                                                        className="btn btn-sm btn-outline-success me-1"
                                                        onClick={() => {
                                                            if (payFor === b._id) {
                                                                setPayFor(null);
                                                                setPayAmount("");
                                                            } else {
                                                                setPayFor(b._id);
                                                                const autoAmount = b.balanceDue > 0 ? b.balanceDue : (b.totalAmount || "");
                                                                setPayAmount(autoAmount > 0 ? autoAmount : "");
                                                                if (b.paymentMethod) setPayMethod(b.paymentMethod);
                                                            }
                                                        }}
                                                    >
                                                        {payFor === b._id ? "Cancel" : "Add Payment"}
                                                    </button>
                                                )}
                                                <button
                                                    className="btn btn-sm btn-outline-info me-1"
                                                    onClick={() => setPreviewBill(b)}
                                                    title="View Invoice Preview (Without Download)"
                                                >
                                                    <i className="fa fa-eye me-1"></i> View
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-primary me-1"
                                                    onClick={() => downloadBillPDF(b, patient, 'digital')}
                                                    title="Download Official Digital Invoice (PDF)"
                                                >
                                                    <i className="fa fa-file-invoice me-1"></i> Invoice
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-secondary me-1"
                                                    onClick={() => downloadBillPDF(b, patient, 'signature')}
                                                    title="Download Physical Signature Copy (PDF)"
                                                >
                                                    <i className="fa fa-file-signature me-1"></i> For Signature
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-success me-1"
                                                    onClick={() => handleSendWhatsApp(b)}
                                                    title="Send via WhatsApp"
                                                >
                                                    <i className="fab fa-whatsapp"></i>
                                                </button>
                                                {b.status !== "Cancelled" && (
                                                    <button
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => handleCancelBill(b._id, b.invoiceNumber)}
                                                    >
                                                        Cancel Bill
                                                    </button>
                                                )}
                                            </td>
                                        )}
                                    </tr>

                                    {payFor === b._id && (
                                        <tr>
                                            <td colSpan={isAdmin ? 8 : 7}>
                                                <div className="row g-2 align-items-end">
                                                    <div className="col-3">
                                                        <label className="form-label small mb-0">Amount</label>
                                                        <input
                                                            type="number"
                                                            min="0.01"
                                                            step="0.01"
                                                            className="form-control form-control-sm"
                                                            value={payAmount}
                                                            onChange={(e) => setPayAmount(e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="col-3">
                                                        <label className="form-label small mb-0">Method</label>
                                                        <select
                                                            className="form-select form-select-sm"
                                                            value={payMethod}
                                                            onChange={(e) => setPayMethod(e.target.value)}
                                                        >
                                                            <option>Cash</option>
                                                            <option>Card</option>
                                                            <option>UPI</option>
                                                            <option>Bank Transfer</option>
                                                            <option>Insurance</option>
                                                            <option>Other</option>
                                                        </select>
                                                    </div>
                                                    <div className="col-3">
                                                        <label className="form-label small mb-0">Reference</label>
                                                        <input
                                                            className="form-control form-control-sm"
                                                            value={payRef}
                                                            onChange={(e) => setPayRef(e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="col-3">
                                                        <button
                                                            className="btn btn-sm btn-success w-100"
                                                            onClick={() => submitPayment(b._id, b.balanceDue)}
                                                            disabled={loading}
                                                        >
                                                            Record Payment
                                                        </button>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <p className="text-muted">No bills yet for this patient.</p>
            )}
            <DeleteConfirmModal
                show={deleteModal.show}
                itemName={deleteModal.name}
                actionType="cancel"
                onClose={() => setDeleteModal({ show: false, id: null, name: '' })}
                onConfirm={confirmDeleteBill}
            />
            <InvoicePreviewModal
                bill={previewBill}
                patient={patient}
                isOpen={Boolean(previewBill)}
                onClose={() => setPreviewBill(null)}
            />
        </Fragment>
    );
}
